import { ICD10_CATALOG, type NoteDiagnosis } from '@ehr/shared';
import type { DxRow } from './noteDiagnosis.js';

/**
 * ehr#741 (Chase 10-31 points 2 and 3, V132 handoff Part 1): the Diagnosis window's list and its
 * tick / Primary rules, kept apart from the markup so they can be tested.
 */

const byCode = (a: DxRow, b: DxRow) => a.code.localeCompare(b.code);

/**
 * Every row the window offers, each code once: the client's treatment plan codes first (in code
 * order), then codes already on the note that the plan doesn't have, then every other code in the
 * ICD-10 list, in code order. A code on the note keeps the label it was saved with.
 */
export function pickerRows(
  planDx: readonly { code: string; label: string }[],
  current: readonly NoteDiagnosis[],
): DxRow[] {
  const seen = new Set<string>();
  const catalogLabel = new Map(ICD10_CATALOG.map(([c, l]) => [c, l]));
  const saved = new Map(current.map((d) => [d.code, d]));
  const plan: DxRow[] = [];
  for (const d of planDx) {
    const code = d.code.trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const on = saved.get(code);
    plan.push({
      code,
      label: on?.label || d.label || catalogLabel.get(code) || '',
      source: on?.source ?? 'tp',
    });
  }
  const extra: DxRow[] = [];
  for (const d of current) {
    if (seen.has(d.code)) continue;
    seen.add(d.code);
    extra.push({
      code: d.code,
      label: d.label || catalogLabel.get(d.code) || '',
      ...(d.source ? { source: d.source } : {}),
    });
  }
  const rest: DxRow[] = [];
  for (const [code, label] of ICD10_CATALOG) {
    if (seen.has(code)) continue;
    seen.add(code);
    rest.push({ code, label, source: 'search' });
  }
  return [...plan.sort(byCode), ...extra.sort(byCode), ...rest];
}

/**
 * V132 search: "F43" matches codes starting F43; words match the start of any word in the name
 * ("dementia", "demen"). Several words must each start a word in the name. Empty matches all.
 */
export function dxMatches(row: DxRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (row.code.toLowerCase().startsWith(q)) return true;
  const words = row.label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return q
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .every((t) => words.some((w) => w.startsWith(t)));
}

/** What is ticked (in list order on save) and which code is Primary. */
export interface DxPick {
  ticked: readonly string[];
  primary: string | null;
}

/** The note's codes ticked, its first one Primary. */
export const initialPick = (value: readonly NoteDiagnosis[]): DxPick => ({
  ticked: value.map((d) => d.code),
  primary: value[0]?.code ?? null,
});

/**
 * Ticking or unticking a code. The first code ticked becomes Primary; unticking the Primary code
 * hands Primary to the next ticked code in the Selected list (the first one when it was last).
 */
export function tickCode(pick: DxPick, rows: readonly DxRow[], code: string, on: boolean): DxPick {
  if (on) {
    if (pick.ticked.includes(code)) return pick;
    return {
      ticked: [...pick.ticked, code],
      primary: pick.primary && pick.ticked.includes(pick.primary) ? pick.primary : code,
    };
  }
  if (!pick.ticked.includes(code)) return pick;
  const ticked = pick.ticked.filter((c) => c !== code);
  if (pick.primary !== code) return { ticked, primary: pick.primary };
  const order = selectedRows(rows, pick.ticked).map((r) => r.code);
  const at = order.indexOf(code);
  const next = order.slice(at + 1).find((c) => c !== code) ?? order.find((c) => c !== code);
  return { ticked, primary: next ?? null };
}

/** Primary can only move to a ticked code. */
export const setPrimary = (pick: DxPick, code: string): DxPick =>
  pick.ticked.includes(code) ? { ...pick, primary: code } : pick;

/** The Selected section: the ticked rows, in list order. */
export const selectedRows = (rows: readonly DxRow[], ticked: readonly string[]): DxRow[] =>
  rows.filter((r) => ticked.includes(r.code));

/** The All diagnoses section: the rows not ticked that match the search. */
export const otherRows = (
  rows: readonly DxRow[],
  ticked: readonly string[],
  query: string,
): DxRow[] => rows.filter((r) => !ticked.includes(r.code) && dxMatches(r, query));
